import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import GameSettings from '../../Settings/GameSettings';
import MenuButtons from './Buttons/MenuButtons';
import { AccueilProvider } from '../../contexts/AccueilContext';
import AudioVisualizer from "../AudioVisualizer/AudioVisualizer";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/LanguageContext';
import SettingsButton from '../Shared/SettingsButton';

const gameSettings = new GameSettings();

const Accueil = () => {
    const navigate = useNavigate();
    const { language, setLanguage, t } = useTranslation();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <AccueilProvider>
            {/* Top-right: settings + language flag + autres jeux */}
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    position: 'fixed',
                    top: { xs: 8, sm: 16 },
                    right: { xs: 8, sm: 16 },
                    zIndex: 10,
                    alignItems: 'center',
                }}
            >
                {!isPhone && <SettingsButton />}
                <Button
                    variant="outlined"
                    onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                    sx={{
                        minWidth: 'auto',
                        padding: { xs: '2px 8px', sm: '4px 12px' },
                        color: '#c191ff',
                        borderColor: 'rgba(193, 145, 255, 0.4)',
                        textTransform: 'none',
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                        cursor: `${gameSettings.getRandomCursor()}, auto`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: '#c191ff',
                            backgroundColor: 'rgba(193, 145, 255, 0.1)',
                        },
                    }}
                >
                    {language === 'fr' ? (
                        <><img src="https://flagcdn.com/w20/gb.png" alt="EN" style={{ width: 16, height: 12, marginRight: 4, verticalAlign: 'middle' }} /> EN</>
                    ) : (
                        <><img src="https://flagcdn.com/w20/fr.png" alt="FR" style={{ width: 16, height: 12, marginRight: 4, verticalAlign: 'middle' }} /> FR</>
                    )}
                </Button>
                {!isPhone && (
                    <Button
                        variant="outlined"
                        onClick={() => window.open('https://jihana.fr', '_blank', 'noopener,noreferrer')}
                        sx={{
                            color: '#c191ff',
                            borderColor: 'rgba(193, 145, 255, 0.4)',
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            cursor: `${gameSettings.getRandomCursor()}, auto`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: '#c191ff',
                                backgroundColor: 'rgba(193, 145, 255, 0.1)',
                            },
                        }}
                    >
                        {t('otherGames')}
                    </Button>
                )}
            </Stack>

            {/* Credits — bottom-right (hidden on phone, shown inline below) */}
            {!isPhone && (
                <Button
                    variant="outlined"
                    onClick={() => navigate('/credits')}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 10,
                        color: '#c191ff',
                        borderColor: 'rgba(193, 145, 255, 0.4)',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        cursor: `${gameSettings.getRandomCursor()}, auto`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: '#c191ff',
                            backgroundColor: 'rgba(193, 145, 255, 0.1)',
                        },
                    }}
                >
                    Credits :)
                </Button>
            )}

            <Box
                display="flex"
                sx={{
                    backgroundColor: '#101728',
                    minHeight: '100vh',
                    '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
                    width: '100vw',
                    flexDirection: isSmall ? 'column' : 'row',
                    justifyContent: 'flex-start',
                    alignItems: isSmall ? 'center' : 'start',
                    cursor: `${gameSettings.getRandomCursor()}, auto`,
                    overflowX: 'hidden',
                }}
            >
                <Stack
                    spacing={isPhone ? 4 : (isSmall ? 6 : 20)}
                    sx={{
                        width: isPhone ? '100%' : (isSmall ? '90%' : '40%'),
                        height: isSmall ? 'auto' : '100%',
                        alignItems: 'center',
                        justifyContent: isPhone ? 'flex-start' : 'start',
                        display: 'flex',
                        paddingLeft: isPhone ? 0 : (isSmall ? 0 : '5%'),
                        paddingRight: isPhone ? 0 : 0,
                        paddingTop: isPhone ? '14vh' : (isSmall ? '12%' : '2%'),
                        paddingBottom: isPhone ? '24px' : 0,
                        boxSizing: 'border-box',
                    }}
                >
                    <Stack
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant={isPhone ? 'h2' : (isSmall ? 'h3' : 'h1')}
                            sx={{ textDecoration: 'line-through', textShadow: '0 0 15px rgba(193, 145, 255, 0.3)' }}
                        >
                            Hitster
                        </Typography>
                        <Typography
                            variant={isPhone ? 'h2' : (isSmall ? 'h3' : 'h1')}
                            sx={{ textShadow: '0 0 30px rgba(193, 145, 255, 0.5), 0 0 60px rgba(193, 145, 255, 0.2)' }}
                        >
                            Hitpster
                        </Typography>
                    </Stack>

                    <Stack
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'flex',
                            paddingX: isPhone ? '20px' : 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        <MenuButtons />
                    </Stack>

                    {isPhone && (
                        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', paddingX: '20px' }}>
                            <Button
                                variant="text"
                                onClick={() => window.open('https://jihana.fr', '_blank', 'noopener,noreferrer')}
                                sx={{
                                    color: '#c191ff',
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    minWidth: 'auto',
                                    padding: '4px 8px',
                                }}
                            >
                                {t('otherGames')}
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => navigate('/credits')}
                                sx={{
                                    color: '#c191ff',
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    minWidth: 'auto',
                                    padding: '4px 8px',
                                }}
                            >
                                Credits :)
                            </Button>
                        </Stack>
                    )}
                </Stack>

                {!isSmall && (
                    <Box
                        display="flex"
                        sx={{
                            height: '100%',
                            width: '60%',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AudioVisualizer/>
                    </Box>
                )}

                <Typography
                    sx={{
                        position: isPhone ? 'static' : 'absolute',
                        bottom: 16,
                        left: 16,
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                        maxWidth: { xs: '90%', sm: '400px' },
                        lineHeight: 1.4,
                        textAlign: isPhone ? 'center' : 'left',
                        margin: isPhone ? '0 auto 16px' : 0,
                        paddingX: isPhone ? '20px' : 0,
                        boxSizing: 'border-box',
                    }}
                >
                    v1.3 — 2026
                    <br />
                    {t('disclaimer')}
                    <br />
                    {t('copyright')}
                </Typography>
            </Box>
        </AccueilProvider>
    );
};

export default Accueil;
